import React, { lazy, Suspense } from 'react';
import { delay } from 'lodash';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import VolumeBar from '../../components/VolumeBar';
import Image from '../../components/Image';
import GigReview from '../../GigReview';
import { enrichMedia } from '../../enrichMedia';
import AudioUtils from './audioUtils';
import AudioLength from './audioLength';

const Waveform = lazy(() => import(/* webpackChunkName: 'Waveform' */'./waveform'));

import '../slide.scss';
import './audio.scss';

const VISUAL_OPTIONS = {
    barWidth: 2,
    waveColor: 'white',
    progressColor: '#1DBF73',
    height: 60,
    normalize: true
};

class AudioSlide extends React.Component {
    constructor(props) {
        super(props);

        this.state = {
            currentPosition: 0,
            totalLength: 0,
            isPlaying: false,
            isLoaded: false,
            isLoading: false,
            volume: AudioUtils.getVolume(),
            isShowingReview: false,
            renderWaveform: false
        };

        this.showReview = this.showReview.bind(this);
        this.hideReview = this.hideReview.bind(this);
        this.flashReview = this.flashReview.bind(this);
        this.toggleReview = this.toggleReview.bind(this);

        this.handleTogglePlay = this.handleTogglePlay.bind(this);
        this.handlePosChange = this.handlePosChange.bind(this);
        this.handlePlayerReady = this.handlePlayerReady.bind(this);
        this.handleVolumeChange = this.handleVolumeChange.bind(this);
        this.handleKeydown = this.handleKeydown.bind(this);
        this.seek = this.seek.bind(this);
        this.loadAudioPlayer = this.loadAudioPlayer.bind(this);
        this.renderAudioPlayer = this.renderAudioPlayer.bind(this);
    }

    componentDidMount() {
        // eslint-disable-next-line react/no-did-mount-set-state
        this.setState({
            renderWaveform: true
        });
    }

    componentWillReceiveProps(nextProps) {
        this.hideReview();
        if (nextProps.isActive && !this.props.isActive) {
            this.flashReview();
        }
        if (!nextProps.isActive && this.props.isActive) {
            this.setState({ isPlaying: false, isLoaded: false });
        }

        // Update volume if it was updated in another audio slide
        if (nextProps.isActive) {
            this.setState({ volume: AudioUtils.getVolume() });
        }
    }

    componentWillUnmount() {
        clearTimeout(this.timerId);
    }

    showReview(autohide) {
        const { slide: { review } } = this.props;
        if (!review) { return; }
        clearTimeout(this.timerId);
        this.setState({ isShowingReview: true });
        if (autohide === true) {
            this.timerId = delay(this.hideReview, 4000);
        }
    }

    hideReview() {
        clearTimeout(this.timerId);
        this.setState({ isShowingReview: false });
    }

    flashReview() {
        this.hideReview();
        this.timerId = delay(this.showReview, 200, true);
    }

    toggleReview() {
        if (this.state.isShowingReview) {
            this.hideReview();
        } else {
            this.showReview();
        }
    }

    handleTogglePlay(e) {
        e && e.preventDefault();

        this.setState({
            isPlaying: !this.state.isPlaying
        });
    }

    handlePosChange(e) {
        const { originalArgs: [currentPosition] } = e;

        this.setState({
            currentPosition
        });
    }

    handlePlayerReady(e) {
        // stupid waveform wrapper will not expose what I need.
        this.setState({
            totalLength: e.wavesurfer.getDuration(),
            isLoaded: true,
            isLoading: false,
            isPlaying: true
        });
    }

    handleKeydown(e) {
        e && e.preventDefault();
        const { volume } = this.state,
            { isActive } = this.props;

        if (!isActive) {
            return;
        }
        AudioUtils.handlePressEvents(e.keyCode, this.handleTogglePlay, this.seek, this.handleVolumeChange, volume);
    }

    handleVolumeChange(volume) {
        if (!AudioUtils.validVolume(volume)) {
            return;
        }
        const newVolume = parseFloat(volume.toFixed(2));

        this.setState({
            volume: newVolume
        }, () => AudioUtils.setVolume(newVolume));
    }

    seek(offset) {
        const { currentPosition, totalLength } = this.state;
        let nextPosition = currentPosition + offset;

        if (nextPosition < 0) {
            nextPosition = 0;
        } else if (nextPosition > totalLength) {
            nextPosition = totalLength;
        }

        this.setState({
            currentPosition: nextPosition
        });
    }

    loadAudioPlayer(e) {
        e && e.preventDefault();

        this.setState({
            isLoading: true
        });
    }

    renderAudioPlayer() {
        const { volume, isPlaying, currentPosition, totalLength, renderWaveform } = this.state;
        const { slide: { audioSrc, title }, isActive } = this.props;

        const className = classNames('fa play-button', isPlaying ? 'fa-pause-circle' : 'fa-play-circle');

        return (
            <div className="audio-player">
                <div className="top-bar">
                    <h4>{ title }</h4>
                    <AudioLength currentPosition={currentPosition} totalLength={totalLength}/>
                    <VolumeBar volume={volume} handleVolumeChange={this.handleVolumeChange}/>
                </div>
                <div className="wrap-bottom-bar">
                    <div className="bottom-bar">
                        <i className={className} onClick={this.handleTogglePlay}/>
                        <div className="waveform">
                            { renderWaveform &&
                            <Suspense fallback={<div/>}>
                                <Waveform audioFile={audioSrc}
                                    pos={this.state.currentPosition}
                                    onPosChange={this.handlePosChange}
                                    onReady={this.handlePlayerReady}
                                    playing={isPlaying && isActive}
                                    options={VISUAL_OPTIONS}
                                    volume={volume}
                                    backend="MediaElement"/>
                            </Suspense>
                            }
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    render() {
        const { isLoading, isLoaded, isShowingReview } = this.state,
            { isActive, expanded, onClick, preload, slide } = this.props,
            { name, review } = slide,
            media = enrichMedia(slide),
            showPlayButton = (!isLoading && !isLoaded),
            showPlayer = (isLoading || isLoaded),
            thumbClasses = classNames('thumbnail', 'audio', { 'in-progress': isLoading }),
            slideClasses = classNames('slide', 'slide-audio', { 'active-review': isShowingReview && isActive }),
            lazyLoad = !isActive && !preload,
            showReview = expanded ? review : review && !showPlayer;

        return (
            <div className={slideClasses} onKeyDown={this.handleKeydown} onClick={onClick}>
                <div className={thumbClasses} tabIndex="0" onClick={this.toggleReview}>
                    { showPlayButton &&
                        <a href="#" className="fa fa-play slide-play-button" onClick={this.loadAudioPlayer}/>
                    }

                    <Image lazyLoad={lazyLoad} isActive={isActive}
                        alt={name} src={expanded ? media.original : media.medium}/>

                    { showPlayer && this.renderAudioPlayer() }
                </div>
                { showReview && <GigReview {...review} compact={!expanded}/> }
            </div>
        );
    }
}

AudioSlide.propTypes = {
    isActive: PropTypes.bool,
    slide: PropTypes.object,
    onClick: PropTypes.func,
    expanded: PropTypes.bool,
    preload: PropTypes.bool
};


export default AudioSlide;
